import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AuditHeroStripComponent } from '../audit/audit-hero-strip.component';
import { AuditStamp } from '../audit/audit.models';
import { PageHelpDialogComponent } from './page-help-dialog.component';

@Component({
  selector: 'app-page-hero',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonModule, AuditHeroStripComponent, PageHelpDialogComponent],
  template: `
    <section class="page-hero">
      <div class="page-hero-title">
        @if (eyebrow) {
          <div class="page-hero-eyebrow">{{ eyebrow }}</div>
        }
        <div class="page-hero-heading">
          <h1>{{ title }}</h1>
          @if (helpManualId || helpVideoId) {
            <p-button label="Help" icon="pi pi-question-circle" severity="secondary" [outlined]="true"
              [ariaLabel]="'Help with ' + title" (onClick)="helpVisible = true"></p-button>
          }
        </div>
        @if (subtitle) {
          <p>{{ subtitle }}</p>
        }
        <div class="page-hero-nav">
          <ng-content select="[nav]"></ng-content>
        </div>
      </div>
      <div class="page-hero-aside">
        <div class="page-hero-actions">
          @if (backLink) {
            <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" [routerLink]="backLink"></p-button>
          }
          <ng-content select="[actions]"></ng-content>
        </div>
        @if (auditStamp) {
          <app-audit-hero-strip [stamp]="auditStamp"></app-audit-hero-strip>
        }
      </div>
    </section>
    @if (helpManualId || helpVideoId) {
      <app-page-help-dialog [visible]="helpVisible" (visibleChange)="helpVisible = $event"
        [pageTitle]="title" [manualId]="helpManualId" [videoId]="helpVideoId"
        [checklist]="helpChecklist"></app-page-help-dialog>
    }
  `,
  styles: [`
    .page-hero {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      padding: 1.25rem;
      border: 1px solid var(--app-border, #dfe7f3);
      border-left: 4px solid var(--app-accent, #0ea5a3);
      border-radius: 8px;
      background: var(--app-surface, #ffffff);
      box-shadow: 0 10px 24px var(--app-shadow-soft, rgba(15, 23, 42, 0.05));
    }

    .page-hero-title {
      min-width: 0;
    }

    .page-hero-heading {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: .75rem;
    }

    .page-hero-eyebrow {
      color: var(--app-accent-strong, #0f766e);
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0;
      margin-bottom: 0.25rem;
      text-transform: uppercase;
    }

    h1 {
      color: var(--app-text, #172033);
      font-size: 2rem;
      line-height: 1.15;
      margin: 0;
    }

    p {
      color: var(--app-muted, #64748b);
      margin: 0.35rem 0 0;
    }

    .page-hero-nav,
    .page-hero-actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .page-hero-nav {
      margin-top: 1rem;
    }

    .page-hero-nav:empty {
      display: none;
    }

    .page-hero-actions {
      justify-content: flex-end;
    }

    .page-hero-aside { display: flex; flex-direction: column; align-items: flex-end; gap: .7rem; min-width: 0; }

    :host ::ng-deep .page-hero-actions > [actions],
    :host ::ng-deep .page-hero-nav > [nav] {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    @media (max-width: 960px) {
      .page-hero {
        align-items: stretch;
        flex-direction: column;
      }

      .page-hero-actions {
        justify-content: flex-start;
      }

      .page-hero-aside { align-items: flex-start; }

      :host ::ng-deep .page-hero-actions > [actions] {
        justify-content: flex-start;
      }
    }

    @media (max-width: 560px) {
      .page-hero {
        padding: 0.85rem;
      }

      h1 {
        font-size: 1.55rem;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageHeroComponent {
  @Input() eyebrow = '';
  @Input() title = '';
  @Input() subtitle = '';
  @Input() backLink?: string | any[];
  @Input() auditStamp?: AuditStamp | null;
  @Input() helpManualId = '';
  @Input() helpVideoId = '';
  @Input() helpChecklist: string[] = [];
  helpVisible = false;
}
